import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import TenantSelector from '../components/TenantSelector';
import { useNavigate } from 'react-router-dom';
import './TenantManager.css';

/**
 * Pagina di gestione dei tenant per l'amministratore
 */
const TenantManager: React.FC = () => {
    const { tenantId, tenantInfo, loading, error, reloadData } = useTenant();
    const navigate = useNavigate();
    const [isAuthorized, setIsAuthorized] = useState(false);

    // Verifica autorizzazione (in una versione reale si userebbe un sistema di autenticazione)
    useEffect(() => {
        // Simulazione: un utente admin ha un token speciale nel localStorage
        const adminToken = localStorage.getItem('adminToken');
        setIsAuthorized(!!adminToken);

        // Se l'utente non è autorizzato, mostra un prompt di password
        if (!adminToken) {
            const password = prompt('Inserisci la password amministrativa:');
            if (password === 'admin123') { // Questa è solo una demo
                localStorage.setItem('adminToken', 'demo-admin-token');
                setIsAuthorized(true);
            }
        }
    }, []);

    // Reindirizza l'utente alla pagina del menu per il tenant selezionato
    const goToTenantMenu = () => {
        if (tenantId) {
            navigate(`/menu?tenantId=${tenantId}`);
        }
    };

    // Se l'utente non è autorizzato, mostra un messaggio di accesso negato
    if (!isAuthorized) {
        return (
            <div className="tenant-manager tenant-manager--unauthorized">
                <h2>Accesso non autorizzato</h2>
                <p>Non hai i permessi necessari per accedere a questa pagina.</p>
                <button onClick={() => navigate('/')}>Torna alla home</button>
            </div>
        );
    }

    return (
        <div className="tenant-manager">
            <h1>Gestione Tenant</h1>

            <div className="tenant-manager__section">
                <h2>Seleziona Tenant</h2>
                <TenantSelector />

                {tenantId && (
                    <div className="tenant-manager__actions">
                        <button
                            className="tenant-manager__action-button"
                            onClick={goToTenantMenu}
                        >
                            Visualizza Menu
                        </button>
                        <button
                            className="tenant-manager__action-button"
                            onClick={() => navigate(`/admin/menu?tenantId=${tenantId}`)}
                        >
                            Modifica Menu
                        </button>
                    </div>
                )}
            </div>

            {tenantId && (
                <div className="tenant-manager__section">
                    <h2>Dettagli Tenant</h2>
                    {loading ? (
                        <p>Caricamento informazioni...</p>
                    ) : error ? (
                        <div className="tenant-manager__error">
                            <p>Errore: {error}</p>
                            <button onClick={reloadData}>Riprova</button>
                        </div>
                    ) : (
                        <div className="tenant-manager__details">
                            <div className="tenant-manager__detail">
                                <strong>ID:</strong> {tenantId}
                            </div>
                            <div className="tenant-manager__detail">
                                <strong>Nome:</strong> {tenantInfo?.nome || 'Non specificato'}
                            </div>
                            {tenantInfo?.indirizzo && (
                                <div className="tenant-manager__detail">
                                    <strong>Indirizzo:</strong> {tenantInfo.indirizzo}
                                </div>
                            )}
                            {tenantInfo?.telefono && (
                                <div className="tenant-manager__detail">
                                    <strong>Telefono:</strong> {tenantInfo.telefono}
                                </div>
                            )}
                            {tenantInfo?.email && (
                                <div className="tenant-manager__detail">
                                    <strong>Email:</strong> {tenantInfo.email}
                                </div>
                            )}
                            {tenantInfo?.sito && (
                                <div className="tenant-manager__detail">
                                    <strong>Sito web:</strong> <a href={tenantInfo.sito} target="_blank" rel="noopener noreferrer">{tenantInfo.sito}</a>
                                </div>
                            )}
                            {tenantInfo?.orariApertura && (
                                <div className="tenant-manager__detail">
                                    <strong>Orari:</strong> {tenantInfo.orariApertura}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="tenant-manager__section">
                <h2>Operazioni Avanzate</h2>
                <div className="tenant-manager__operations">
                    <button
                        className="tenant-manager__operation-button"
                        onClick={() => navigate('/admin/tenants/new')}
                    >
                        Crea Nuovo Tenant
                    </button>
                    {tenantId && (
                        <>
                            <button
                                className="tenant-manager__operation-button"
                                onClick={() => navigate(`/admin/tenants/edit?tenantId=${tenantId}`)}
                            >
                                Modifica Tenant
                            </button>
                            <button
                                className="tenant-manager__operation-button tenant-manager__operation-button--danger"
                                onClick={() => {
                                    if (window.confirm(`Sei sicuro di voler eliminare il tenant "${tenantInfo?.nome || tenantId}"?`)) {
                                        alert('Operazione simulata: il tenant sarebbe stato eliminato');
                                    }
                                }}
                            >
                                Elimina Tenant
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TenantManager;
